import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { FormattedMessage, injectIntl, IntlShape } from '@liveauctioneers/hammer-ui-core/intl';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { TextAreaInput } from '@liveauctioneers/hammer-ui-core/textArea';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { Component } from 'react';
import isEqual from 'react-fast-compare';

import PageTitle from '@/components/PageTitle/PageTitle';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { EventContext } from '@/redux/modules/analytics';
import { submitConsignment } from '@/redux/modules/consignment';
import { ModalContext } from '@/redux/modules/modal';
import { ConsignmentForm } from '@/types/Consignment';
import { Seller } from '@/types/Seller';
import { preventDefault } from '@/utils/preventDefault';
import { required } from '@/utils/validation';

import ConsignmentsPageHeaderMeta from './ConsignmentsPageHeaderMeta';

const defaultState = {
    aquireCode: '',
    aquireCodeValid: true,
    categories: [],
    categoriesValid: true,
    description: '',
    descriptionValid: true,
    file: null,
    other: '',
    photo: null,
    photoValid: true,
};

type Props = {
    baseUrl: string;
    emailAddress: string;
    intl: IntlShape;
    openConfirmConsignmentModal: Function;
    seller: Seller;
    sellerId: number;
    submitConsignment: (form: ConsignmentForm, file: File) => ReturnType<ReturnType<typeof submitConsignment>>;
    submitConsignmentStatus: any;
    userLoggedIn: boolean;
};

type State = {
    aquireCode: string;
    aquireCodeValid: boolean;
    categories: any[];
    categoriesValid: boolean;
    description: string;
    descriptionValid: boolean;
    file: File;
    other: string;
    photo: any;
    photoValid: boolean;
};

const modalContext: ModalContext = {
    eventContext: EventContext.consign,
    id: 0,
};

class ConsignmentsPage extends Component<Props, State> {
    state = defaultState;

    shouldComponentUpdate = (nextProps, nextState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    fileUpload: HTMLInputElement | null | undefined;

    onSubmitConsignment = () => {
        const { aquireCode, categories, description, file, other } = this.state;

        let valid = this.onValidating(description, 'descriptionValid');
        valid = valid && this.onValidating(aquireCode, 'aquireCodeValid');
        valid = valid && this.onValidating(categories, 'categoriesValid');
        valid = valid && this.onValidating(file, 'photoValid');

        if (valid) {
            this.props
                .submitConsignment(
                    {
                        description,
                        email: this.props.emailAddress,
                        multi2: categories.join(', '),
                        other: `${other} - ${aquireCode}`,
                    },
                    file
                )
                .then(() => {
                    this.setState(defaultState);
                    return this.props.openConfirmConsignmentModal();
                });
        }
    };

    onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files[0];

        if (file && file.size < 10000000 && (file.type === 'image/jpeg' || file.type === 'image/png')) {
            const reader = new FileReader();
            reader.onload = (upload) => {
                this.setState({
                    file,
                    photo: upload.target.result,
                    photoValid: true,
                });
            };

            reader.readAsDataURL(file);
        } else {
            this.setState({ file: null, photo: null, photoValid: false });
            return;
        }
    };

    onValidating = (value, validField) => {
        let validState = required(value);
        if (validField === 'photoValid') {
            validState = Boolean(value);
        }
        const newState = {};
        newState[validField] = validState;
        this.setState(newState);
        return validState;
    };

    onValueChange = (event) => {
        const newState = {};
        const { id, value } = event.target;
        newState[id] = value;
        this.setState(newState);
        this.onValidating(id, `${id}Valid`);
    };

    onCheckboxChange = ({ currentTarget }) => {
        const { checked, name } = currentTarget;
        const categories = [...this.state.categories];
        if (checked) {
            categories.push(name);
        } else {
            const index = categories.indexOf(name);
            categories.splice(index, 1);
        }
        this.setState({
            categories,
            categoriesValid: Boolean(categories.length),
        });
    };

    handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const aquireCode = event.currentTarget.value;
        return this.setState({
            aquireCode,
            aquireCodeValid: true,
        });
    };

    render() {
        const { baseUrl, emailAddress, intl, seller, sellerId, submitConsignmentStatus, userLoggedIn } = this.props;

        const { error, submitted } = submitConsignmentStatus;

        const { aquireCode, aquireCodeValid, categoriesValid, description, descriptionValid, other, photoValid } =
            this.state;

        const { formatMessage } = intl;

        const header = sellerId ? (
            <FormattedMessage
                id="how_to_consign_items_to_seller"
                values={{ sellerName: seller.name }}
            />
        ) : (
            <FormattedMessage id="consign_with_trusted_auction_houses" />
        );
        const consign_1 = sellerId ? (
            <FormattedMessage
                id="consign_description_1_seller"
                values={{
                    sellerName: seller.name,
                    title: (
                        <span
                            className="font-bold"
                            key="consign-description-1-title"
                        >
                            <FormattedMessage id="consign_description_1_title" />
                        </span>
                    ),
                }}
            />
        ) : (
            <FormattedMessage id="consign_description_1" />
        );
        const consign_2 = sellerId ? (
            <FormattedMessage
                id="consign_description_2_seller"
                values={{
                    sellerName: seller.name,
                    title: (
                        <span
                            className="font-bold"
                            key="consign-description-2-title"
                        >
                            <FormattedMessage id="consign_description_2_title" />
                        </span>
                    ),
                }}
            />
        ) : (
            <FormattedMessage id="consign_description_2" />
        );

        return (
            <div className="mx-auto max-w-[640px] px-sm pb-lg">
                <ConsignmentsPageHeaderMeta baseUrl={baseUrl} />
                <div className="max-h-[5000px] transition-[max-height] duration-[1.5s] [&_h1]:text-center [&_h2]:pt-lg [&_h2]:pb-sm [&.hide]:max-h-0 [&.hide]:overflow-hidden">
                    <PageTitle title={header} />
                    <div className="[&_p]:mb-lg [&_p]:text-center">
                        <p>{consign_1}</p>
                        <p>{consign_2}</p>
                        <p>
                            <FormattedMessage
                                id="consign_description_3"
                                values={{
                                    title: (
                                        <span
                                            className="font-bold"
                                            key="consign-description-3-title"
                                        >
                                            <FormattedMessage id="consign_description_3_title" />
                                        </span>
                                    ),
                                }}
                            />
                        </p>
                        <div
                            className="mt-md ml-md flex max-w-[640px] flex-col items-stretch justify-start gap-md p-0"
                            onSubmit={preventDefault}
                        >
                            {userLoggedIn && (
                                <TextInput
                                    aria-label={formatMessage({
                                        id: 'email_address',
                                    })}
                                    disabled
                                    id="email"
                                    labelText={formatMessage({
                                        id: 'email_address',
                                    })}
                                    value={emailAddress}
                                />
                            )}
                            <TextAreaInput
                                aria-label={formatMessage({
                                    id: 'general_description',
                                })}
                                className="[&_textarea]:h-[44px]"
                                data-testid="generalDescriptionInput"
                                errorText={String(
                                    formatMessage(
                                        { id: 'enter_a_valid' },
                                        { token: formatMessage({ id: 'description' }) }
                                    )
                                )}
                                hasError={!descriptionValid}
                                helpText={
                                    <span className="whitespace-normal">
                                        ({formatMessage({ id: 'description_help' })})
                                    </span>
                                }
                                id="description"
                                labelText={formatMessage({
                                    id: 'general_description',
                                })}
                                onChange={this.onValueChange}
                                value={description}
                            />
                            <div>
                                <LabelMini color="secondary">
                                    <FormattedMessage id="how_did_you_aquire" />
                                </LabelMini>
                                <div className="mt-md flex flex-col gap-xs">
                                    <Radio.Option
                                        checked={aquireCode === 'I aquired this item through a purchase'}
                                        className="m-0"
                                        data-testid="boughtItRadio"
                                        name="aquireCode"
                                        onChange={this.handleRadioChange}
                                        value="I aquired this item through a purchase"
                                    >
                                        <FormattedMessage id="i_bought_it" />
                                    </Radio.Option>
                                    <Radio.Option
                                        checked={aquireCode === 'I inherited this item'}
                                        className="m-0"
                                        name="aquireCode"
                                        onChange={this.handleRadioChange}
                                        value="I inherited this item"
                                    >
                                        <FormattedMessage id="i_inherited_it" />
                                    </Radio.Option>
                                    <Radio.Option
                                        checked={aquireCode === 'I found this item'}
                                        className="m-0"
                                        name="aquireCode"
                                        onChange={this.handleRadioChange}
                                        value="I found this item"
                                    >
                                        <FormattedMessage id="i_found_it" />
                                    </Radio.Option>
                                    <Radio.Option
                                        checked={aquireCode === 'I was given this item'}
                                        className="m-0"
                                        name="aquireCode"
                                        onChange={this.handleRadioChange}
                                        value="I was given this item"
                                    >
                                        <FormattedMessage id="it_was_a_gift_it" />
                                    </Radio.Option>
                                    {!aquireCodeValid && (
                                        <div className="text-text-critical">
                                            <FormattedMessage
                                                id="enter_a_valid"
                                                values={{
                                                    token: formatMessage({
                                                        id: 'value',
                                                    }).toLowerCase(),
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <LabelMini
                                    className="uppercase"
                                    color="secondary"
                                >
                                    <FormattedMessage id="category" />
                                </LabelMini>
                                <div className="mt-md flex flex-wrap gap-xs">
                                    <section data-testid="20thCenturyCheckbox">
                                        <Checkbox.Option
                                            className="mr-md"
                                            name="20th Century"
                                            onChange={this.onCheckboxChange}
                                        >
                                            {formatMessage({ id: 'twentieth_century' })}
                                        </Checkbox.Option>
                                    </section>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Autographs"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'autographs' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Books"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'books' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Commercial"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'commercial' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Decorative Arts"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'decorative_arts' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Entertainment"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'entertainment' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Fine Art"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'fine_art' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Furniture"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'navigation.furniture' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Rugs/Carpets"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'rugs_and_carpets' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Toys"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'toys' })}
                                    </Checkbox.Option>

                                    <Checkbox.Option
                                        className="mr-md"
                                        name="Other"
                                        onChange={this.onCheckboxChange}
                                    >
                                        {formatMessage({ id: 'other' })}
                                    </Checkbox.Option>
                                </div>
                                {!categoriesValid && (
                                    <div className="text-text-critical">
                                        <FormattedMessage
                                            id="enter_a_valid"
                                            values={{
                                                token: formatMessage({
                                                    id: 'category',
                                                }).toLowerCase(),
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            <TextAreaInput
                                aria-label={`${formatMessage({ id: 'other' })} ${formatMessage({ id: 'optional_parenthesis' })}`}
                                className="[&_textarea]:h-[44px]"
                                errorText={String(
                                    formatMessage({ id: 'enter_a_valid' }, { token: formatMessage({ id: 'other' }) })
                                )}
                                helpText={
                                    <span className="whitespace-normal">({formatMessage({ id: 'other_help' })})</span>
                                }
                                id="other"
                                labelText={`${String(formatMessage({ id: 'other' }))} ${String(formatMessage({ id: 'optional_parenthesis' }))}`}
                                onChange={this.onValueChange}
                                value={other}
                            />
                            <div className="flex flex-col gap-2xs">
                                <label
                                    className="text-sm font-medium text-text-primary"
                                    htmlFor="photo"
                                >
                                    {formatMessage({ id: 'photo' })}
                                </label>
                                <input
                                    accept=".jpg, .png"
                                    aria-label={formatMessage({ id: 'photo' })}
                                    id="photo"
                                    onChange={this.onFileChange}
                                    type="file"
                                />
                                {!photoValid && (
                                    <span className="text-sm text-text-critical">
                                        {formatMessage({ id: 'please_choose_a_photo' })}
                                    </span>
                                )}
                            </div>
                            {Boolean(this.state.photo) && (
                                <img
                                    className="mb-lg max-w-[640px]"
                                    src={this.state.photo}
                                />
                            )}
                            <ShouldLogInContainer
                                modalContext={modalContext}
                                requireCompleteProfile
                                requireLogin
                                runFunction={this.onSubmitConsignment}
                            >
                                <Button
                                    className="mb-sm min-w-[160px]"
                                    data-testid="submitButton"
                                    loading={submitted}
                                    style="primary"
                                    type="submit"
                                >
                                    <FormattedMessage id="submit" />
                                </Button>
                            </ShouldLogInContainer>
                            {error && (
                                <div className="text-text-critical">
                                    <FormattedMessage id="there_was_an_error_submitting_consignment" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default injectIntl(ConsignmentsPage);
